import {
  EventTimeContext,
  getEventTimeZone,
  reinterpretTimestamp,
} from '@libs/EventTime'

const SCHEDULE_FIELDS = [
  'startDateTime',
  'entryDateTime',
  'endDateTime',
  'registrationCloseDateTime',
] as const

type ScheduleField = (typeof SCHEDULE_FIELDS)[number]
const SCHEDULE_FIELD_LABELS: Record<ScheduleField, string> = {
  startDateTime: 'Event start',
  entryDateTime: 'Entry time',
  endDateTime: 'Event end',
  registrationCloseDateTime: 'Registration close',
}
type EventSchedule = EventTimeContext &
  Partial<Record<ScheduleField, string | null>>

/**
 * Optimistically mirrors the backend's timezone-only update semantics: each
 * schedule value keeps its wall-clock components while its UTC instant moves.
 */
export const setEventTimeZoneId = <T extends EventSchedule>(
  event: T | undefined,
  timeZoneId: string
): T & { timeZoneId: string } => {
  const current = event ?? ({} as T)
  const previousTimeZoneId = getEventTimeZone(current)

  if (!previousTimeZoneId || previousTimeZoneId === timeZoneId) {
    return { ...current, timeZoneId }
  }

  const shiftedSchedule: Partial<Record<ScheduleField, string | null>> = {}
  for (const field of SCHEDULE_FIELDS) {
    const value = current[field]
    if (!value) continue

    const conversion = reinterpretTimestamp(
      value,
      previousTimeZoneId,
      timeZoneId
    )
    if (conversion.status !== 'valid') {
      throw new Error(`${SCHEDULE_FIELD_LABELS[field]}: ${conversion.message}`)
    }
    shiftedSchedule[field] = conversion.value
  }

  return { ...current, ...shiftedSchedule, timeZoneId }
}
